// =============================================================================
// Admin Analytics — shared widget chrome
// =============================================================================
// Reusable pieces shared by every widget:
//  - useWidgetTimeOverride: per-widget local override of the global date range
//  - WidgetIconButton: an icon button matching the AnalyticsStatCard "raw data"
//    button box exactly (h-6 w-6, rounded-sm, limestone border)
//  - WidgetToolbar: [time-filter][raw-data] buttons, right-aligned
//  - WidgetBadge: honest labelling for caveated metrics
// =============================================================================
import { ArrowUturnLeftIcon, CircleStackIcon, FunnelIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";

import { CalendarDateRange, CalendarPicker, Label, Tooltip } from "@fronterahealth/frontera-ui-components";

import { HeroIconType } from "@pages/AdminAnalytics/components/types";

// ----- Per-widget time override ----------------------------------------------

export interface WidgetTimeOverride {
  /** The widget's local range, or undefined when it follows the global range. */
  override: CalendarDateRange | undefined;
  setOverride: (range: CalendarDateRange) => void;
  clearOverride: () => void;
  isOverridden: boolean;
}

/** Local state for a single widget's time-range override. */
export const useWidgetTimeOverride = (): WidgetTimeOverride => {
  const [override, setOverride] = useState<CalendarDateRange | undefined>(undefined);
  return {
    override,
    setOverride,
    clearOverride: () => setOverride(undefined),
    isOverridden: override !== undefined,
  };
};

// ----- Icon button (mirrors the AnalyticsStatCard raw-data button) -----------

interface WidgetIconButtonProps {
  Icon: HeroIconType;
  tooltip: string;
  onClick?: () => void;
  /** Tinted/active treatment (e.g. an active time override). */
  active?: boolean;
  dataTestId?: string;
}

export const WidgetIconButton: React.FC<WidgetIconButtonProps> = ({ Icon, tooltip, onClick, active, dataTestId }) => (
  <button
    type="button"
    onClick={onClick}
    data-test-id={dataTestId}
    aria-label={tooltip}
    className={clsx(
      "flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-solid p-1 transition-colors",
      active ? "border-limestone-300 bg-limestone-50" : "border-limestone-100 bg-white hover:bg-limestone-50",
    )}
  >
    <Tooltip text={tooltip} showInfoIcon={false}>
      <Icon className={clsx("h-4 w-4", active ? "text-limestone-900" : "text-text-secondary")} />
    </Tooltip>
  </button>
);

// ----- Time-override control (icon button + popover with a CalendarPicker) ----

interface TimeOverrideControlProps {
  control: WidgetTimeOverride;
  /** Optional extra content rendered in the popover below the time picker (e.g. a category filter). */
  filterSlot?: React.ReactNode;
  /** When true, lights up the dot indicator independent of the time override (e.g. a category is non-default). */
  extraActive?: boolean;
  /** Called when the reset button is clicked, alongside clearing the time override. Use to reset extra filters (e.g. category back to "all"). */
  onResetExtra?: () => void;
}

const TimeOverrideControl: React.FC<TimeOverrideControlProps> = ({
  control,
  filterSlot,
  extraActive,
  onResetExtra,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  return (
    <div className="relative" ref={wrapperRef}>
      <WidgetIconButton
        Icon={FunnelIcon}
        tooltip="Filter"
        onClick={() => setOpen((v) => !v)}
        dataTestId="widget-time-override"
      />
      {(control.isOverridden || !!extraActive) && (
        <span className="pointer-events-none absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-globemallow-500" />
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={close} aria-hidden />
          <div
            className="absolute right-0 top-full z-30 mt-2 w-[300px] origin-top-right rounded-lg border border-limestone-100 bg-white p-3 shadow-lg"
            style={{ animation: "aa-rise 140ms ease-out" }}
          >
            <div className="mb-3 flex items-center justify-between">
              <Label size="small" displayType="loud" colorType="primary">
                Override Filters
              </Label>
              {(control.isOverridden || !!extraActive) && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-text-secondary hover:text-primary"
                  onClick={() => {
                    control.clearOverride();
                    onResetExtra?.();
                    close();
                  }}
                >
                  <ArrowUturnLeftIcon className="h-3 w-3" />
                  Reset to global
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label size="extra-small" displayType="loud" colorType="primary">
                Override time range
              </Label>
              <CalendarPicker
                placeholder="Date"
                mode="filter"
                selectedDateRange={control.override}
                selectedLabel={control.override?.selectedLabel}
                onDateChange={(range) => range.startDate && range.endDate && control.setOverride(range)}
                containerClass="[&_[role=button]]:text-xs"
              />
            </div>
            {filterSlot && <div className="mt-3 border-t border-limestone-100 pt-3">{filterSlot}</div>}
          </div>
        </>
      )}
    </div>
  );
};

// ----- Toolbar ----------------------------------------------------------------

interface WidgetToolbarProps {
  timeOverride: WidgetTimeOverride;
  /** Opens the raw-data drill-down. Omit for widgets with no underlying table. */
  onViewData?: () => void;
  /** Extra content rendered inside the filter popover below the time picker. */
  filterSlot?: React.ReactNode;
  /** Lights up the dot when something inside the popover is non-default (e.g. category filter). */
  extraActive?: boolean;
  /** Resets extra filters (e.g. category) when the reset button is clicked. */
  onResetExtra?: () => void;
  /** Additional icon buttons rendered to the left of the filter button (e.g. a view-mode toggle). */
  extraButtons?: React.ReactNode;
}

/** The two-button toolbar: [extraButtons] [time-filter] [raw-data]. */
export const WidgetToolbar: React.FC<WidgetToolbarProps> = ({
  timeOverride,
  onViewData,
  filterSlot,
  extraActive,
  onResetExtra,
  extraButtons,
}) => (
  <div className="flex shrink-0 items-center gap-2">
    {extraButtons}
    <TimeOverrideControl
      control={timeOverride}
      filterSlot={filterSlot}
      extraActive={extraActive}
      onResetExtra={onResetExtra}
    />
    {onViewData && (
      <WidgetIconButton Icon={CircleStackIcon} tooltip="Raw Data" onClick={onViewData} dataTestId="widget-raw-data" />
    )}
  </div>
);

// ----- Badges & assumption notes ---------------------------------------------

type WidgetBadgeKind = "catalight" | "beta" | "estimated";

const BADGE_STYLES: Record<WidgetBadgeKind, { label: string; className: string }> = {
  catalight: { label: "Catalight-linked", className: "bg-blue-50 text-blue-700" },
  beta: { label: "Beta", className: "bg-purple-50 text-purple-700" },
  estimated: { label: "Estimated", className: "bg-limestone-100 text-text-secondary" },
};

export const WidgetBadge: React.FC<{ kind: WidgetBadgeKind }> = ({ kind }) => {
  const { label, className } = BADGE_STYLES[kind];
  return (
    <span className={clsx("rounded-full px-2 py-0.5 text-[10px] font-medium leading-none", className)}>{label}</span>
  );
};
